import type { ReactNode } from 'react';
import { isBlankProseHtml } from '@/document-render/application/isBlankProseHtml';
import type { QuoteProseEditorState } from '@/document-render/presentation/editing/quote/QuoteCanvasEditingContext';
import type { ProseEditorCloseOptions } from '@/document-render/presentation/editing/proseEditorSlot';

export interface ProseQuoteBodyProps {
  kind: string;
  /** The RAW stored body (tokens kept) — what the editor edits and what a commit is compared with. */
  bodyHtml: string;
  onEditProse: (kind: string, bodyHtml: string) => void;
  proseEditor?: QuoteProseEditorState;
  /** The resolved, sliced body the export path renders — shown whenever nothing is being edited. */
  children: ReactNode;
}

/**
 * The quote canvas's prose body (LLAMS-610 DB-05; INT-03 / QS-05). Not editing, it is exactly the
 * export path's resolved HTML. Editing, the host's editor replaces it in place over the RAW body,
 * and a commit writes `onEditProse(kind, html)` only when the edited HTML differs from the stored
 * body and still has text — an empty write would leave the step uncompletable.
 */
export function ProseQuoteBody({ kind, bodyHtml, onEditProse, proseEditor, children }: ProseQuoteBodyProps) {
  if (!proseEditor?.isEditing) return children;

  const onCommit = (next: string, options?: ProseEditorCloseOptions) => {
    proseEditor.stop(options);
    if (next === bodyHtml || isBlankProseHtml(next)) return;
    onEditProse(kind, next);
  };

  return (
    <div data-testid={`document-render:prose-quote-editor:${kind.replaceAll('_', '-')}`}>
      {proseEditor.render({ kind, html: bodyHtml, onCommit, onCancel: (options) => proseEditor.stop(options) })}
    </div>
  );
}
